import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, Zap, CheckCircle, XCircle, MessageSquare, AlertTriangle, RefreshCw } from 'lucide-react';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
const ZPC_ID = 'marvis-labs';

interface Evidence {
  n_examples: number;
  mean_quality: number;
  adoption_count: number;
  source_patterns: string[];
  partial: boolean;
}

interface ProposalItem {
  inbox_id: string;
  skill: string;
  status: string;
  quality_delta: number;
  evidence: Evidence;
  estimated_impact: { quality_improvement_pct: number; time_saved_sec: number };
  proposal_path: string;
  created_at: string;
  expires_at?: string;
}

interface HealthStatus {
  pool_reachable: boolean;
  last_sync_at: string | null;
  circuit_breaker: string;
  pending_count: number;
  stale_count: number;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const QualityBadge: React.FC<{ delta: number }> = ({ delta }) => (
  <span
    aria-label={`quality delta plus ${delta.toFixed(1)}`}
    style={{
      background: 'var(--hover-overlay)',
      color: 'var(--accent-amber)',
      border: '1px solid var(--accent-amber)',
      borderRadius: 6,
      padding: '1px 7px',
      fontSize: '0.72rem',
      fontWeight: 700,
      fontFamily: 'var(--font-mono)',
      whiteSpace: 'nowrap',
    }}
  >
    {delta >= 0 ? '+' : ''}{delta.toFixed(1)} Δ
  </span>
);

const EvidenceChip: React.FC<{ evidence: Evidence }> = ({ evidence }) => (
  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
    {evidence.n_examples} ZPCs · mean {evidence.mean_quality.toFixed(1)}/5
    · {evidence.adoption_count} adopters
    {evidence.partial && ' ⚠ partial'}
  </span>
);

const HealthBar: React.FC<{ health: HealthStatus | null; loading: boolean }> = ({ health, loading }) => {
  if (loading || !health) return null;
  const fresh = health.last_sync_at !== null;
  const dot = fresh ? 'var(--accent-teal)' : 'var(--accent-amber)';
  const text = fresh
    ? `Pool synced ${health.last_sync_at?.slice(0, 10)} · ${health.pending_count} pending`
    : `Pool sync stale · ${health.pending_count} pending`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem',
      color: 'var(--text-secondary)', padding: '6px 0' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} />
      {text}
      {health.stale_count > 0 && (
        <span style={{ color: 'var(--accent-amber)', marginLeft: 6 }}>
          <AlertTriangle size={12} style={{ display: 'inline' }} /> {health.stale_count} stale
        </span>
      )}
    </div>
  );
};

const ProposalCard: React.FC<{
  proposal: ProposalItem;
  selected: boolean;
  onSelect: () => void;
}> = ({ proposal, selected, onSelect }) => {
  const age = proposal.created_at
    ? Math.floor((Date.now() - new Date(proposal.created_at).getTime()) / 86400000)
    : 0;
  const isStale = age >= 30;
  return (
    <article
      role="button"
      aria-expanded={selected}
      aria-label={`Proposal for ${proposal.skill}, quality delta ${proposal.quality_delta >= 0 ? '+' : ''}${proposal.quality_delta.toFixed(1)}`}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      style={{
        background: selected ? 'var(--active-overlay)' : 'var(--bg-elevated)',
        border: `1px solid ${selected ? 'var(--accent-amber)' : 'var(--border-subtle)'}`,
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 8,
        cursor: 'pointer',
        outline: 'none',
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem', flex: 1 }}>
          {proposal.skill}
        </span>
        <QualityBadge delta={proposal.quality_delta} />
        {isStale && (
          <span style={{ fontSize: '0.7rem', color: 'var(--accent-amber)' }}>
            Expiring soon
          </span>
        )}
      </div>
      <EvidenceChip evidence={proposal.evidence} />
      <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
        {age === 0 ? 'today' : `${age}d ago`}
      </div>
    </article>
  );
};

const ProposalDetailPanel: React.FC<{
  proposal: ProposalItem;
  content: string;
  resolving: boolean;
  onResolve: (decision: string, comment: string) => void;
  onClose: () => void;
}> = ({ proposal, content, resolving, onResolve, onClose }) => {
  const [confirm, setConfirm] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [activeSection, setActiveSection] = useState<'inputs' | 'process' | 'outputs'>('process');

  // Extract a section from the raw content for display
  const getSectionContent = (section: string) => {
    const re = new RegExp(`### (?:Current|Proposed).*${section}.*?\\n([\\s\\S]*?)(?=###|$)`, 'i');
    const m = content.match(re);
    return m ? m[1].trim().slice(0, 800) : '(no content for this section)';
  };

  const strength = proposal.quality_delta >= 0.7 ? 'Strong evidence: apply this improvement'
    : 'Borderline evidence: review carefully before approving';

  return (
    <div
      role="dialog"
      aria-labelledby="proposal-panel-title"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: 'var(--bg-panel)', borderLeft: '1px solid var(--border-default)',
        display: 'flex', flexDirection: 'column', zIndex: 50, overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 id="proposal-panel-title" style={{ flex: 1, fontSize: '0.9rem', fontWeight: 700,
          color: 'var(--text-primary)', margin: 0 }}>
          Proposal: {proposal.skill}
        </h2>
        <QualityBadge delta={proposal.quality_delta} />
        <button onClick={onClose} aria-label="Close panel" style={{ background: 'none', border: 'none',
          color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
      </div>

      <div style={{ padding: '16px 20px', flex: 1 }}>
        {/* Evidence */}
        <section style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)',
            marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Evidence</h3>
          <table style={{ fontSize: '0.8rem', width: '100%', borderCollapse: 'collapse' }}>
            <caption className="sr-only">Evidence table for {proposal.skill} proposal</caption>
            <tbody>
              <tr>
                <td style={{ color: 'var(--text-secondary)', padding: '3px 0' }}>Contributing ZPCs</td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{proposal.evidence.n_examples}</td>
              </tr>
              <tr>
                <td style={{ color: 'var(--text-secondary)', padding: '3px 0' }}>Mean quality</td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  {proposal.evidence.mean_quality.toFixed(1)}/5
                </td>
              </tr>
              <tr>
                <td style={{ color: 'var(--text-secondary)', padding: '3px 0' }}>Adopters</td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{proposal.evidence.adoption_count}</td>
              </tr>
              <tr>
                <td style={{ color: 'var(--text-secondary)', padding: '3px 0' }}>Impact estimate</td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  +{proposal.estimated_impact.quality_improvement_pct}% quality
                  · {proposal.estimated_impact.time_saved_sec}s saved/run
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Diff section tabs */}
        <section style={{ marginBottom: 20 }}>
          <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {(['inputs', 'process', 'outputs'] as const).map(s => (
              <button
                key={s} role="tab" aria-selected={activeSection === s}
                onClick={() => setActiveSection(s)}
                style={{
                  padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600,
                  borderRadius: 4, border: 'none', cursor: 'pointer',
                  background: activeSection === s ? 'var(--active-overlay)' : 'transparent',
                  color: activeSection === s ? 'var(--accent-amber)' : 'var(--text-secondary)',
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div role="tabpanel" style={{
            background: 'var(--bg-elevated)', borderRadius: 6, padding: 12,
            maxHeight: '30vh', overflowY: 'auto',
            fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
            color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
          }}>
            {getSectionContent(activeSection) || 'No changes in this section.'}
          </div>
        </section>

        {/* Recommendation banner */}
        <div style={{
          background: 'var(--hover-overlay)', border: '1px solid var(--accent-amber)',
          borderRadius: 6, padding: '8px 12px', marginBottom: 20,
          fontSize: '0.8rem', color: 'var(--accent-amber)',
        }}>
          {strength}
        </div>

        {/* Resolution controls */}
        {confirm ? (
          <div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-primary)', marginBottom: 12 }}>
              {confirm === 'reject'
                ? 'Reject this proposal? This cannot be undone.'
                : `Confirm ${confirm} this proposal?`}
            </p>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Optional: reason for this decision"
              rows={2}
              style={{
                width: '100%', marginBottom: 10, background: 'var(--bg-elevated)',
                border: '1px solid var(--border-default)', borderRadius: 4,
                color: 'var(--text-primary)', fontSize: '0.8rem', padding: 8, resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                disabled={resolving}
                onClick={() => onResolve(confirm, comment)}
                style={{
                  flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: confirm === 'approve' ? 'var(--accent-amber)' : 'var(--bg-elevated)',
                  color: confirm === 'approve' ? '#000' : 'var(--text-primary)',
                  fontWeight: 700, fontSize: '0.85rem',
                  opacity: resolving ? 0.6 : 1,
                }}
                aria-label={`Confirm ${confirm} improvement proposal for ${proposal.skill}`}
              >
                {resolving ? '…' : `Confirm ${confirm.charAt(0).toUpperCase() + confirm.slice(1)}`}
              </button>
              <button
                onClick={() => { setConfirm(null); setComment(''); }}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border-default)',
                  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={() => setConfirm('approve')}
              aria-label={`Approve improvement proposal for ${proposal.skill}`}
              style={{
                padding: '10px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: 'var(--accent-amber)', color: '#000', fontWeight: 700, fontSize: '0.9rem',
              }}
            >
              Approve
            </button>
            <button
              onClick={() => setConfirm('reject')}
              aria-label={`Reject improvement proposal for ${proposal.skill}`}
              style={{
                padding: '8px 0', borderRadius: 6,
                border: '1px solid var(--border-default)',
                background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
              }}
            >
              Reject
            </button>
            <button
              onClick={() => setConfirm('modify')}
              aria-label={`Request changes for improvement proposal for ${proposal.skill}`}
              style={{
                padding: '8px 0', borderRadius: 6,
                border: '1px solid var(--border-subtle)',
                background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer',
                fontSize: '0.82rem',
              }}
            >
              Request Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main View ─────────────────────────────────────────────────────────────────

export const LibraryProposalsView: React.FC = () => {
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const [proposals, setProposals] = useState<ProposalItem[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [selected, setSelected] = useState<ProposalItem | null>(null);
  const [content, setContent] = useState('');
  const [resolving, setResolving] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [skillFilter, setSkillFilter] = useState('');

  const fetchProposals = useCallback(async (status: string) => {
    setLoading(true);
    setError('');
    try {
      const url = skillFilter
        ? `${API_BASE}/api/zpc/${ZPC_ID}/proposals?status=${status}&skill=${encodeURIComponent(skillFilter)}&limit=50`
        : `${API_BASE}/api/zpc/${ZPC_ID}/proposals?status=${status}&limit=50`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProposals(data.proposals ?? []);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load proposals');
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [skillFilter]);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zpc/${ZPC_ID}/proposals/health`);
      if (res.ok) setHealth(await res.json());
    } catch {}
    setHealthLoading(false);
  }, []);

  const fetchContent = useCallback(async (inboxId: string) => {
    setContent('');
    try {
      const res = await fetch(`${API_BASE}/api/zpc/${ZPC_ID}/proposals/${inboxId}/content`);
      if (res.ok) setContent(await res.text());
    } catch {}
  }, []);

  useEffect(() => {
    fetchProposals(tab === 'pending' ? 'pending' : 'resolved');
    fetchHealth();
  }, [tab, fetchProposals, fetchHealth]);

  useEffect(() => {
    if (selected) fetchContent(selected.inbox_id);
  }, [selected, fetchContent]);

  const handleResolve = async (decision: string, comment: string) => {
    if (!selected) return;
    setResolving(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/zpc/${ZPC_ID}/approvals/${selected.inbox_id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision, comment }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message ?? `HTTP ${res.status}`);
      }
      const verb = decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'flagged for changes';
      setToast(`Proposal ${verb} — ${decision === 'approve' ? 'skill update queued' : 'no further action'}`);
      setSelected(null);
      fetchProposals('pending');
      setTimeout(() => setToast(''), 4000);
    } catch (err: any) {
      setToast(`Error: ${err.message}`);
      setTimeout(() => setToast(''), 5000);
    } finally {
      setResolving(false);
    }
  };

  // Unique skills for filter chips
  const skillOptions = Array.from(new Set(proposals.map(p => p.skill))).sort();

  return (
    <div style={{ padding: '24px 32px', maxWidth: 800, position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div role="status" style={{
          position: 'fixed', bottom: 24, right: 24, background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)', borderRadius: 8, padding: '10px 18px',
          fontSize: '0.85rem', color: 'var(--text-primary)', zIndex: 200,
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <BookOpen size={20} style={{ color: 'var(--accent-amber)' }} />
        <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Library Proposals
        </h1>
        {proposals.length > 0 && tab === 'pending' && (
          <span style={{
            background: 'var(--accent-amber)', color: '#000', borderRadius: 99,
            padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700,
          }}>
            {proposals.length}
          </span>
        )}
        <button
          onClick={() => fetchProposals(tab === 'pending' ? 'pending' : 'resolved')}
          aria-label="Refresh proposals"
          style={{ marginLeft: 'auto', background: 'none', border: 'none',
            color: 'var(--text-secondary)', cursor: 'pointer' }}
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <HealthBar health={health} loading={healthLoading} />

      {/* Tab bar */}
      <div role="tablist" style={{ display: 'flex', gap: 0, marginBottom: 16,
        borderBottom: '1px solid var(--border-subtle)' }}>
        {(['pending', 'history'] as const).map(t => (
          <button
            key={t} role="tab" aria-selected={tab === t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 18px', background: 'none', border: 'none',
              borderBottom: tab === t ? '2px solid var(--accent-amber)' : '2px solid transparent',
              color: tab === t ? 'var(--accent-amber)' : 'var(--text-secondary)',
              fontWeight: tab === t ? 700 : 400, cursor: 'pointer',
              fontSize: '0.85rem', marginBottom: -1,
            }}
          >
            {t === 'pending' ? 'Pending' : 'History'}
          </button>
        ))}
      </div>

      {/* Skill filter chips */}
      {skillOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          <button
            onClick={() => setSkillFilter('')}
            style={{
              padding: '3px 10px', borderRadius: 99, fontSize: '0.75rem', cursor: 'pointer',
              border: '1px solid var(--border-default)',
              background: !skillFilter ? 'var(--active-overlay)' : 'transparent',
              color: !skillFilter ? 'var(--accent-amber)' : 'var(--text-secondary)',
            }}
          >
            All
          </button>
          {skillOptions.map(s => (
            <button
              key={s}
              onClick={() => setSkillFilter(skillFilter === s ? '' : s)}
              style={{
                padding: '3px 10px', borderRadius: 99, fontSize: '0.75rem', cursor: 'pointer',
                border: '1px solid var(--border-default)',
                background: skillFilter === s ? 'var(--active-overlay)' : 'transparent',
                color: skillFilter === s ? 'var(--accent-amber)' : 'var(--text-secondary)',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      {loading ? (
        <div
          aria-busy="true"
          aria-label="Loading proposals"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              height: 68, borderRadius: 8, background: 'var(--bg-elevated)',
              opacity: 0.4 + i * 0.15,
            }} />
          ))}
        </div>
      ) : error ? (
        <div role="alert" style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Could not load proposals. <button onClick={() => fetchProposals('pending')}
            style={{ color: 'var(--accent-amber)', background: 'none', border: 'none',
              cursor: 'pointer' }}>Retry ↺</button>
        </div>
      ) : proposals.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem', padding: '32px 0',
          textAlign: 'center' }}>
          {tab === 'pending'
            ? 'No pending proposals. Pool patterns are compared daily.'
            : 'No resolved proposals yet.'}
        </div>
      ) : (
        <div
          aria-live="polite"
          aria-label={`${proposals.length} proposals`}
        >
          {proposals.map(p => (
            <ProposalCard
              key={p.inbox_id}
              proposal={p}
              selected={selected?.inbox_id === p.inbox_id}
              onSelect={() => setSelected(selected?.inbox_id === p.inbox_id ? null : p)}
            />
          ))}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <ProposalDetailPanel
          proposal={selected}
          content={content}
          resolving={resolving}
          onResolve={handleResolve}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};
